import {useCallback, useEffect, useState, type MutableRefObject} from 'react';

import Constants from 'utils/constants';

type Props = {
    keys: string[];
    refs?: MutableRefObject<Record<string, HTMLDivElement>>;
    isolated?: boolean;
    onEnter?: (key: string) => void;
    submitControls?: string[];
}

type Result = {
    activeKey: string | undefined;
}

const DEFAULT_SUBMIT_CONTROLS = [Constants.KeyCodes.ENTER[0]];

export const useListKeysControl = ({
    keys,
    onEnter,
    refs,
    isolated,
    submitControls = DEFAULT_SUBMIT_CONTROLS,
}: Props): Result => {
    const [activeKey, setActiveKey] = useState<string | undefined>(keys[0]);

    useEffect(() => {
        setActiveKey(keys[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keys.toString()]);

    const selectElement = useCallback(
        (e: KeyboardEvent, key: string, scrollIntoViewOptions?: ScrollIntoViewOptions) => {
            e.preventDefault();
            refs?.current[key]?.scrollIntoView({
                block: 'nearest',
                inline: 'nearest',
                behavior: 'smooth',
                ...scrollIntoViewOptions,
            });
            setActiveKey(key);
        }, [refs]);

    const onKeyDown = useCallback((e: KeyboardEvent) => {
        if (keys.length === 0) {
            return;
        }
        const lastIndex = keys.length - 1;

        if (!activeKey) {
            if (keys[0] && e.key === Constants.KeyCodes.DOWN[0]) {
                selectElement(e, keys[0]!, {block: 'end', inline: 'nearest'});
                return;
            }
            if (keys[lastIndex] && e.key === Constants.KeyCodes.UP[0]) {
                selectElement(e, keys[lastIndex]!, {block: 'start', inline: 'nearest'});

                return;
            }
        }

        if (activeKey && onEnter && submitControls.includes(e.key)) {
            if (isolated) {
                e.stopPropagation();
                e.preventDefault();
            }

            onEnter(activeKey);
            setActiveKey(undefined);
        }

        if (activeKey && e.key === Constants.KeyCodes.UP[0]) {
            if (isolated) {
                e.stopPropagation();
                e.preventDefault();
            }
            const activeIndex = keys.indexOf(activeKey);

            if (activeIndex === 0) {
                selectElement(e, keys[lastIndex]!, {block: 'start', inline: 'nearest'});
                return;
            }

            selectElement(e, keys[activeIndex - 1]!);
        }

        if (activeKey && e.key === Constants.KeyCodes.DOWN[0]) {
            if (isolated) {
                e.stopPropagation();
                e.preventDefault();
            }
            const activeIndex = keys.indexOf(activeKey);

            if (activeIndex === lastIndex) {
                selectElement(e, keys[0]!, {block: 'end', inline: 'nearest'});
                return;
            }

            selectElement(e, keys[activeIndex + 1]!);
        }
    }, [keys, activeKey, onEnter, selectElement, isolated, submitControls]);

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown, isolated);

        return () => {
            document.removeEventListener('keydown', onKeyDown, isolated);
        };
    }, [onKeyDown, refs, isolated]);

    return {
        activeKey,
    };
};
